import { useEffect, useState } from "react";

interface TypedTextProps {
  /** Word/phrase to type out. Changing it restarts the animation (EN ⇄ AR). */
  text: string;
  className?: string;
  cursorClassName?: string;
  typeSpeed?: number;
  deleteSpeed?: number;
  holdFull?: number;
  holdEmpty?: number;
}

/**
 * Custom typing/deleting effect — no external library.
 * Plain useState + setTimeout, blinking cursor via CSS animate-pulse.
 * Starts empty so SSR markup and hydration stay in sync.
 */
export function TypedText({
  text,
  className = "",
  cursorClassName = "",
  typeSpeed = 110,
  deleteSpeed = 55,
  holdFull = 1800,
  holdEmpty = 500,
}: TypedTextProps) {
  const [count, setCount] = useState(0);
  const [deleting, setDeleting] = useState(false);

  // Restart cleanly whenever the source text (i.e. the language) changes.
  useEffect(() => {
    setCount(0);
    setDeleting(false);
  }, [text]);

  useEffect(() => {
    if (!text) return;

    let delay = deleting ? deleteSpeed : typeSpeed;
    if (!deleting && count === text.length) delay = holdFull;
    if (deleting && count === 0) delay = holdEmpty;

    const timer = setTimeout(() => {
      if (!deleting) {
        if (count === text.length) setDeleting(true);
        else setCount((c) => c + 1);
      } else {
        if (count === 0) setDeleting(false);
        else setCount((c) => c - 1);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [count, deleting, text, typeSpeed, deleteSpeed, holdFull, holdEmpty]);

  return (
    <span className={`relative inline-block whitespace-nowrap align-baseline ${className}`}>
      {/* Reserves the full width/height so the line never reflows or wraps mid-animation. */}
      <span aria-hidden="true" className="invisible">
        {text}
      </span>
      <span aria-hidden="true" className="absolute inset-0 flex items-baseline">
        <span>{text.slice(0, count)}</span>
        <span className={`inline-block animate-pulse font-light ${cursorClassName}`}>|</span>
      </span>
      <span className="sr-only">{text}</span>
    </span>
  );
}
